import React, { useMemo } from 'react';
import TaxMastersScreen from 'components/shared/tax-master-module/TaxMastersScreen';
import GridLoadingSkeleton from 'components/common/GridLoadingSkeleton';
import IconSystem from 'icon-collection/icon-systems';
import { EmptyStatePill, EnterpriseScreenShell } from 'components/enterprise-shell';
import { taxMasterService } from 'api/services/taxMasterService';
import { useTaxPack } from 'hooks/api/useTaxPack';

/**
 * App-side host for the RELOCATABLE tax-master module (TaxCore ADR-0022 §3). This file and
 * the two service adapters are the only app-specific pieces; the module folder itself is
 * upstream-owned and delta-synced.
 *
 * Nothing is hard-coded: the jurisdiction, its component codes and its published slab bases
 * come from the application's default pack (GET /api/v1/tax/pack), which the API resolves
 * from `TaxModule:DefaultJurisdictionCode` and the tenant's own tax schema. A
 * multi-jurisdiction application resolves the entity's jurisdiction first and passes it to
 * `useTaxPack`.
 *
 * The layout passes `moduleIcon` (the menu row's icon key) and `moduleName` (the tab title)
 * to every registered screen; both are forwarded so the header icon and the minimize-strip
 * labels follow the catalog rather than a hard-coded value.
 *
 * Registered in ComponentRegistry as TaxMastersHost; menu item `tax-master` under Property
 * Settings (icon `percent`). The layout passes the role-resolved `allowedActions`.
 */
export interface TaxMastersHostProps {
  tabId?: string;
  moduleIcon?: string;
  moduleName?: string;
  allowedActions?: string[];
}

const TaxMastersHost: React.FC<TaxMastersHostProps> = ({ tabId, moduleIcon, moduleName, allowedActions }) => {
  const { pack, loading, error, refetch } = useTaxPack();

  const bases = useMemo(() => (pack?.bases ?? []).map((b) => ({
    value: b.basisCode,
    label: b.label,
    description: [b.unitOfMeasure, b.basisScope].filter(Boolean).join(' · ') || null,
  })), [pack]);

  if (loading || error || !pack) {
    // Same header as the screen, so it does not jump in when the pack arrives; only the grid
    // area shows the skeleton. A tenant behind the schema floor lands on the error with the
    // server's MIGRATION_PENDING message; so does a tenant whose default jurisdiction is not
    // configured. Both are operator actions, not user errors, and the message says which.
    return (
      <EnterpriseScreenShell
        title="Tax Masters"
        description="User-configured tax rates: flat, slab and cess — versioned by effective date"
        icon={<IconSystem.percent size={18} />}
        tabId={tabId}
      >
        {loading ? (
          <div className="data-grid-container"><GridLoadingSkeleton rows={10} columns={6} /></div>
        ) : (
          <EmptyStatePill
            variant="error"
            title="Tax configuration unavailable"
            message={error ?? 'The tax pack could not be loaded for this tenant.'}
            onRefresh={() => { void refetch(); }}
          />
        )}
      </EnterpriseScreenShell>
    );
  }

  return (
    <TaxMastersScreen
      tabId={tabId}
      moduleIcon={moduleIcon}
      moduleName={moduleName}
      api={taxMasterService}
      jurisdictionCode={pack.jurisdictionCode}
      componentCodes={pack.components.map((c) => c.code)}
      bases={bases}
      allowedActions={allowedActions}
    />
  );
};

export default TaxMastersHost;
